import { Helmet } from 'react-helmet-async';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import EditNoteIcon from '@mui/icons-material/EditNote';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PaymentsIcon from '@mui/icons-material/Payments';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ViewListIcon from '@mui/icons-material/ViewList';
import { Button, Link, Typography } from '@mui/material';
import { Box, Container } from '@mui/system';

import { CONFIG } from '~/config';

import HeroMenuButtons, { menuItem } from '~/components/HeroMenuButtons';
import IcontTextXColumns from '~/components/IcontTextXColumns';
import ItineraryPackages from '~/components/ItineraryPackages';

// Reuse the published WordPress guide's sample documents, not separate frontend copies.
const itinerarySamples = [
  {
    title: 'Flight itinerary sample (real scheduled flights)',
    description:
      'Shows the passenger name, route, dates and real flights scheduled for the journey. It does not reserve a seat or include an airline booking reference. Any Tripenger order reference is for the order only.',
    filename: 'sample_schedule_only_itinerary.pdf',
    downloadLabel: 'Download flight itinerary sample (PDF)',
  },
  {
    title: 'Flight reservation sample (with booking reference)',
    description:
      'Shows where the airline booking reference (PNR) appears. If you purchase a Tripenger itinerary with a PNR, you can use your own reference to check the reservation on the airline’s website, subject to the airline’s verification and ticketing limits. The reference in this sample is fictional.',
    filename: 'sample_pnr_itinerary.pdf',
    downloadLabel: 'Download flight reservation sample (PDF)',
  },
];

const focusStyles = {
  '&:focus-visible': {
    outline: '2px solid currentColor',
    outlineOffset: '4px',
  },
};

function ItineraryHomepage() {
  const infoSiteBaseUrl = CONFIG.app.helpCenterLink.replace(/\/+$/, '');
  const menuItems = [menuItem.searchFlight, menuItem.visaItinerary];
  const canonicalUrl = 'https://www.tripenger.com/itinerary';
  const pageTitle = 'Request Travel Itinerary - Visa Application Documents | Tripenger';
  const pageDescription =
    'Request a professional, visa-ready travel itinerary for embassy submissions, travel insurance, business trips, and vacation planning.';
  const serviceStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Tripenger Travel Itinerary Request',
    description: pageDescription,
    provider: {
      '@type': 'Organization',
      name: 'Tripenger',
      url: 'https://www.tripenger.com/',
    },
    serviceType: 'Travel itinerary document preparation',
    url: canonicalUrl,
  };
  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Search Flights',
        item: 'https://www.tripenger.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Request Itinerary',
        item: canonicalUrl,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta content={pageDescription} name="description" />
        <meta
          content="travel itinerary generator, visa application documents, embassy requirements, travel insurance documentation, business travel planner, vacation scheduler"
          name="keywords"
        />
        <meta
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
          name="robots"
        />
        <link href={canonicalUrl} rel="canonical" />
        <meta content={pageTitle} property="og:title" />
        <meta content={pageDescription} property="og:description" />
        <meta content={canonicalUrl} property="og:url" />
        <meta content="website" property="og:type" />
        <meta content="Tripenger" property="og:site_name" />
        <meta content="summary_large_image" name="twitter:card" />
        <meta content={pageTitle} name="twitter:title" />
        <meta content={pageDescription} name="twitter:description" />
        <script type="application/ld+json">{JSON.stringify(serviceStructuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbStructuredData)}</script>
      </Helmet>

      <Box
        bgcolor="primary.dark"
        color="#fff"
        pb={8}
        pt={5}
        sx={{
          '@media (max-width:1111px)': {
            pt: 4,
          },
        }}
      >
        <Container>
          <HeroMenuButtons menuItems={menuItems} />
          <Typography
            component="h1"
            sx={{ mb: 2, mt: 3, fontWeight: 700, color: '#fff' }}
            variant="h4"
          >
            Select a package below to get your professionally prepared visa-ready itinerary.
          </Typography>
          <Box
            aria-label="Help choosing an itinerary"
            component="aside"
            sx={{
              mb: 4,
              p: { xs: 2, sm: 2.5 },
              bgcolor: 'secondary.main',
              border: '1px solid',
              borderColor: 'secondary.light',
              borderRadius: 2,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              gap: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
              <MenuBookIcon sx={{ color: 'primary.main', fontSize: 28, mt: 0.5 }} />
              <Box>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }} variant="body2">
                  Need help choosing?
                </Typography>
                <Link
                  color="inherit"
                  href={`${infoSiteBaseUrl}/flight-itinerary-for-visa-application/`}
                  sx={{
                    ...focusStyles,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 44,
                    fontSize: { xs: '1rem', sm: '1.0625rem' },
                    fontWeight: 700,
                    lineHeight: 1.4,
                    textUnderlineOffset: '4px',
                    '&:hover': { textDecorationThickness: '2px' },
                  }}
                  underline="always"
                >
                  Learn more about flight itineraries for visa applications
                  <ArrowForwardIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                </Link>
                <Typography sx={{ mt: 0.75, color: 'grey.300', lineHeight: 1.6 }} variant="body2">
                  Our guide explains itinerary types, what you receive, and common questions.
                </Typography>
              </Box>
            </Box>
            <Button
              component="a"
              href="#itinerary-samples"
              startIcon={<DescriptionIcon />}
              sx={{
                ...focusStyles,
                color: 'common.white',
                borderColor: 'secondary.light',
                borderRadius: 1.5,
                minHeight: 48,
                px: 2,
                py: 1.25,
                flexShrink: 0,
                maxWidth: { md: 260 },
                textTransform: 'none',
                fontWeight: 700,
                lineHeight: 1.5,
                '&:hover': { bgcolor: 'primary.dark', borderColor: 'common.white' },
              }}
              variant="outlined"
            >
              Preview sample itineraries before ordering
            </Button>
          </Box>
          <Box
            aria-label="Itinerary packages"
            component="section"
            id="itinerary-packages"
            sx={{ scrollMarginTop: 12 }}
          >
            <ItineraryPackages />
          </Box>
        </Container>
      </Box>
      <Box
        aria-labelledby="itinerary-samples-heading"
        component="section"
        id="itinerary-samples"
        sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', scrollMarginTop: 12 }}
      >
        <Container>
          <Typography
            component="h2"
            id="itinerary-samples-heading"
            sx={{ mb: 1, fontWeight: 700 }}
            variant="h3"
          >
            Preview sample itineraries
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Watermarked examples only — not active bookings, issued tickets, or documents to submit
            with a visa application.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
            {itinerarySamples.map(sample => (
              <Box
                key={sample.filename}
                sx={{
                  p: 3,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography component="h3" sx={{ mb: 1 }} variant="h5">
                  {sample.title}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                  {sample.description}
                </Typography>
                <Button
                  color="secondary"
                  component="a"
                  download
                  endIcon={<DownloadIcon />}
                  href={`${infoSiteBaseUrl}/wp-content/uploads/2026/10/${sample.filename}`}
                  startIcon={<PictureAsPdfIcon />}
                  sx={{
                    ...focusStyles,
                    color: 'secondary.main',
                    minHeight: 44,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                  variant="outlined"
                >
                  {sample.downloadLabel}
                </Button>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: { xs: 'stretch', sm: 'center' } }}>
            <Button
              component="a"
              href="#itinerary-packages"
              startIcon={<ArrowUpwardIcon />}
              sx={{
                ...focusStyles,
                minHeight: 48,
                width: { xs: '100%', sm: 'auto' },
                px: 3,
                textTransform: 'none',
                fontWeight: 700,
              }}
              variant="contained"
            >
              Back to itinerary packages
            </Button>
          </Box>
        </Container>
      </Box>
      <Box
        aria-labelledby="itinerary-steps-heading"
        component="section"
        sx={{ pb: { xs: 6, md: 10 }, pt: 2, bgcolor: 'background.paper' }}
      >
        <Container>
          <Typography
            component="h2"
            id="itinerary-steps-heading"
            sx={{ mb: 3, fontWeight: 700, color: 'text.primary' }}
            variant="h3"
          >
            How it works
          </Typography>
          <IcontTextXColumns
            iconContents={[
              {
                icon: ViewListIcon,
                content: {
                  title: '1. Select package from options above',
                  description:
                    'Choose the itinerary package that best fits your travel needs and timeline to get started in minutes.',
                },
              },
              {
                icon: EditNoteIcon,
                content: {
                  title: '2. Provide Itinerary details',
                  description:
                    'Share your destination, travel dates, passport information, and trip preferences so we can prepare an accurate itinerary for your application.',
                },
              },
              {
                icon: PaymentsIcon,
                content: {
                  title: '3. Pay',
                  description:
                    'Complete your payment securely using our trusted checkout to confirm your itinerary request instantly.',
                },
              },
              {
                icon: DescriptionIcon,
                content: {
                  title: '4. Receive itinerary',
                  description:
                    'Receive your professionally prepared travel itinerary document, ready for visa applications and travel planning.',
                },
              },
            ]}
          />
        </Container>
      </Box>
    </>
  );
}

export default ItineraryHomepage;
